import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ReactPlayer from 'react-player/file';
import { Box, Stack, Typography } from '@mui/material';

import { Delete, Download, Livestream, NextEvent, PreviousEvent } from 'components/events/actions';
import { Close, Fullscreen, PlayPause, Seeker, Sound, Timer } from 'components/events/controls';
import { EventsVideoOverlay } from 'components/events/EventsVideoOverlay';
import { EventVideoPlaceholder } from 'components/events/EventVideoPlaceholder';
import { useAspenLogger } from 'hooks/useAspenLogger';
import { useAspenSocket } from 'hooks/useAspenSocket';
import { formatDuration } from 'modules/time';

import { ActivityState } from 'types/aspen/events';
import { AspenSocketMsg, GetVideoRxMsg } from 'types/aspen/socketMessages';
import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './eventsVideo.module.scss';

dayjs.extend(duration);

interface EventsVideoProps {
  event: ActivityState;
  leaf: AppDeviceLeaf | null;
  previousEventId?: string;
  nextEventId?: string;
}

interface PlayerState {
  url: string;
  fullscreen: boolean;
  playing: boolean;
  seeking: boolean;
  isSeeking: boolean;
  controls: boolean;
  volume: number;
  muted: boolean;
  duration: number;
  played: number; // 1 is true
  loaded: number; // 1 is true
  loadedSeconds: number;
  playedSeconds: number;
  rotation: number; // Video rotation from getVideo response
}

const DEFAULT_PLAYER_STATE = {
  url: '',
  fullscreen: false,
  playing: true,
  seeking: false,
  isSeeking: false,
  controls: false,
  volume: 1,
  muted: false,
  duration: 0,
  played: 0,
  loaded: 0,
  loadedSeconds: 0,
  playedSeconds: 0,
  rotation: 0,
};

const GET_VIDEO_DEBOUNCE_TIMEOUT = 250; // 250ms debounce for getVideo requests

export const EventsVideo = forwardRef(({ event, leaf, previousEventId, nextEventId }: EventsVideoProps, _ref) => {
  const { logAspenEvent } = useAspenLogger();
  const { getVideo, subscribe, unsubscribe } = useAspenSocket();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<Error>();
  const [needsProcessing, setNeedsProcessing] = React.useState<boolean>(false);

  const [fullscreenEnabled, setfullscreenEnabled] = useState<boolean>(false);
  const [showControls, setShowControls] = useState(false);
  const showControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playerState, setPlayerState] = React.useState<PlayerState>({
    ...DEFAULT_PLAYER_STATE,
    duration: event?.parameters?.videoMeta?.duration || 0,
  });

  // Handle processing state changes
  React.useEffect(() => {
    if (needsProcessing) {
      setLoading(false);
      setPlayerState((prevState) => ({
        ...prevState,
        url: '',
        playing: false,
      }));
    }
  }, [needsProcessing]);

  const currentEventId = useRef<string | null>(null);
  const currentLeafId = useRef<string | null>(null);
  const getVideoTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const playerRef = useRef<ReactPlayer | null>(null);
  const setPlayerRef = useCallback((player: ReactPlayer) => {
    if (player && !playerRef.current) {
      playerRef.current = player;
    }
  }, []);
  const containerRef = useRef<HTMLElement | null>(null);

  const ended = playerState.played === 1;
  const isOverlayVisible = loading || ended || error || needsProcessing;

  const handleOnReplay = () => {
    setPlayerState((prevState) => ({
      ...prevState,
      playing: true,
      playedSeconds: 0,
      played: 0,
    }));
  };

  const handlePlayPause = () => {
    setPlayerState((prevState) => ({
      ...prevState,
      playing: !prevState.playing,
    }));
  };

  useEffect(() => {
    subscribe((messageEvent: MessageEvent) => {
      const message: AspenSocketMsg = JSON.parse(messageEvent.data);

      if (message.type === 'video' || message.type === 'get_video') {
        const msg = message as GetVideoRxMsg;
        // Log the video response event
        logAspenEvent({
          log_id: 'iot.web.event.video.response',
          payload: {
            request_id: msg.id,
            success: !msg.error && !!msg.payload.url,
            ...(!msg.error && { has_video: !!msg.payload.url }),
            ...(msg.error && { error: msg.error }),
          },
        });

        if (msg.error) {
          setLoading(false);
          setError(new Error(msg.error));
          setPlayerState((prevState) => ({
            ...prevState,
            playing: false,
          }));
        } else {
          const { url, rotation } = msg.payload;
          const needsProc = rotation !== 0;
          setNeedsProcessing(needsProc);
          setPlayerState((prevState) => ({
            ...prevState,
            url: needsProc ? '' : url, // Only set URL if video doesn't need processing
            rotation: rotation || 0, // Store rotation from getVideo response
          }));
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (event && (currentEventId.current !== event.id || currentLeafId.current !== leaf?.id)) {
      setLoading(true);
      setError(undefined); // Reset error state for new event
      setNeedsProcessing(false); // Reset processing state for new event
      setPlayerState({
        ...DEFAULT_PLAYER_STATE,
        duration: event?.parameters?.videoMeta?.duration || 0,
      });

      if (getVideoTimeout.current) {
        clearTimeout(getVideoTimeout.current);
      }

      // Debounce the video request
      getVideoTimeout.current = setTimeout(() => {
        getVideo({ payload: { leafId: leaf?.id || event?.deviceId, stateId: event.id } });
      }, GET_VIDEO_DEBOUNCE_TIMEOUT);

      currentEventId.current = event.id;
      currentLeafId.current = leaf?.id || null;
    }
  }, [leaf, event]);

  return (
    <Box
      id={`videoContainer-${event.id}`}
      ref={containerRef}
      className={`${styles.videoContainer} ${fullscreenEnabled ? styles.fullscreen : ''}`}
      data-testid="component-events-video"
      onMouseEnter={() => {
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setShowControls(false);
      }}
      onMouseMove={() => {
        if (fullscreenEnabled) {
          if (showControlsTimeout.current) {
            clearTimeout(showControlsTimeout.current);
          }
          setShowControls(true);
          showControlsTimeout.current = setTimeout(() => {
            setShowControls(false);
          }, 3000);
        }
      }}
    >
      <Box className={styles.videoGrid}>
        <Stack
          className={`${styles.header} ${showControls && styles.visible}`}
          direction="row"
          justifyContent="space-between"
          onBlur={() => {
            if (fullscreenEnabled) {
              setShowControls(false);
            }
          }}
          onMouseMove={(e) => {
            if (fullscreenEnabled && showControlsTimeout.current) {
              e.stopPropagation();
              clearTimeout(showControlsTimeout.current);
            }
          }}
        >
          <Box className={styles.eventInformation}>
            <Typography className={styles.eventTitle} variant="h6">
              {leaf?.name || '(Unknown device)'}
            </Typography>
            <Stack className={styles.eventDescription} direction="row">
              <Typography variant="body2">
                <>
                  {dayjs(event.timestamp).format('MMM DD, YYYY')}
                  <Box component="span" className={styles.dotDivider} />
                </>

                {dayjs(event.timestamp).format('hh:mm A')}
                {playerState.duration ? (
                  <>
                    <Box component="span" className={styles.dotDivider} />
                    {formatDuration(playerState.duration)}
                  </>
                ) : null}
              </Typography>
            </Stack>
          </Box>
          <Box className={styles.closeButton}>
            <Close />
          </Box>
        </Stack>

        <Box className={`${styles.arrowLeft} ${!previousEventId ? styles.hidden : ''}`}>
          {previousEventId ? <PreviousEvent previousEventId={previousEventId} currentEventId={event.id} /> : null}
        </Box>

        <Box className={styles.videoWrapper}>
          <ReactPlayer
            ref={setPlayerRef}
            id={`video-${event.id}`}
            className={styles.videoPlayer}
            url={playerState.url}
            playing={playerState.playing && !playerState.isSeeking}
            controls={playerState.controls}
            volume={playerState.volume}
            muted={playerState.muted}
            width="100%"
            height="initial"
            config={{
              attributes: {
                onContextMenu: (e: Event) => e.preventDefault(),
              },
            }}
            playsinline
            playIcon={<div />}
            previewTabIndex={0}
            onClick={handlePlayPause}
            onReady={() => {
              setLoading(false);

              const player = playerRef.current;

              if (player) {
                const internalPlayer = player.getInternalPlayer() as HTMLVideoElement;

                setPlayerState((prevState) => ({
                  ...prevState,
                  playing: true,
                  duration: internalPlayer.duration || prevState.duration,
                }));
              }
            }}
            onProgress={() => {
              const player = playerRef.current;

              if (player) {
                const internalPlayer = player.getInternalPlayer() as HTMLVideoElement;

                const { buffered } = internalPlayer;
                const bufferedEnd = buffered?.length ? buffered.end(buffered.length - 1) : 0;

                setPlayerState((prevState) => ({
                  ...prevState,
                  loadedSeconds: bufferedEnd,
                  loaded: bufferedEnd / prevState.duration,
                }));
              }
            }}
            onError={(err: Error) => {
              // Log the video error event
              logAspenEvent({
                log_id: 'iot.web.event.video.error',
                payload: {
                  device_id: leaf?.id,
                  event_id: event.id,
                  error: err.message,
                },
              });

              setLoading(false);
              setError(err);
            }}
            onTimeUpdate={() => {
              const player = playerRef.current;

              if (player) {
                const internalPlayer = player.getInternalPlayer();

                setPlayerState((prevState) => ({
                  ...prevState,
                  playedSeconds: internalPlayer.currentTime,
                  played: internalPlayer.currentTime / prevState.duration,
                }));
              }
            }}
            onPause={() => {
              setPlayerState((prevState) => ({
                ...prevState,
                playing: false,
              }));
            }}
            onPlay={() => {
              // Log the video play event
              logAspenEvent({
                log_id: 'iot.web.event.video.play',
                payload: {
                  device_id: leaf?.id,
                  event_id: event.id,
                },
              });

              setPlayerState((prevState) => ({
                ...prevState,
                playing: true,
              }));
            }}
            onEnded={() => {
              setPlayerState((prevState) => ({
                ...prevState,
                playing: false,
              }));
            }}
          />
          <EventVideoPlaceholder event={event} show={loading || !!error || needsProcessing} />
          {isOverlayVisible && (
            <EventsVideoOverlay
              loading={loading}
              error={error}
              ended={ended}
              processing={needsProcessing}
              onReplay={handleOnReplay}
            />
          )}
        </Box>

        <Box className={`${styles.arrowRight} ${!nextEventId ? styles.hidden : ''}`}>
          {nextEventId ? <NextEvent nextEventId={nextEventId} currentEventId={event.id} /> : null}
        </Box>

        <Stack
          direction="column"
          className={`${styles.controls}  ${showControls && styles.visible}`}
          onBlur={() => {
            if (fullscreenEnabled) {
              setShowControls(false);
            }
          }}
          onMouseMove={(e) => {
            if (fullscreenEnabled && showControlsTimeout.current) {
              e.stopPropagation();
              clearTimeout(showControlsTimeout.current);
            }
          }}
        >
          <Box className={styles.seeker}>
            <Seeker
              elapsed={playerState.playedSeconds}
              duration={playerState.duration}
              disabled={needsProcessing}
              isPlaying={playerState.playing && !loading}
              loading={loading}
              onSeekingChange={(seeking) => {
                setPlayerState((prevState) => ({
                  ...prevState,
                  isSeeking: seeking,
                }));
              }}
              handleOnSeek={(_event, newValue) => {
                if (needsProcessing) return; // Don't allow seeking during processing
                const player = playerRef.current;
                if (player) {
                  const internalPlayer = player.getInternalPlayer();

                  setPlayerState((prevState) => ({
                    ...prevState,
                    playedSeconds: newValue as number,
                  }));

                  internalPlayer.currentTime = newValue as number;
                }
              }}
            />
          </Box>
          <Stack direction="row" className={styles.buttons}>
            <Stack direction="row" spacing={4} className={styles.videoControls} alignItems="center">
              <PlayPause
                playing={playerState.playing && !loading}
                disabled={needsProcessing}
                togglePlaying={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
              />
              <Timer elapsedTime={playerState.playedSeconds} totalTime={playerState.duration} />
            </Stack>
            <Stack direction="row" spacing={4} className={styles.eventActions}>
              <Sound
                soundEnabled={!playerState.muted}
                toggleSound={() => {
                  setPlayerState((prevState) => ({
                    ...prevState,
                    muted: !prevState.muted,
                  }));
                }}
              />
              <Download
                source={playerState.url}
                deviceName={leaf?.name}
                timestamp={event.timestamp}
                playerRef={playerRef}
                rotation={playerState.rotation}
                disabled={needsProcessing}
              />
              <Delete
                eventId={event.id}
                deviceId={leaf?.id || event.deviceId}
                onDeleteStart={() => {
                  logAspenEvent({
                    log_id: 'iot.web.event.interaction',
                    payload: {
                      event_id: event.id,
                      interaction: 'delete',
                    },
                  });
                }}
              />
              <Livestream leafId={leaf?.id as string} />
              <Fullscreen
                isFullscreen={fullscreenEnabled}
                disabled={needsProcessing}
                toggleFullscreen={() => {
                  if (containerRef.current) {
                    if (fullscreenEnabled) {
                      document.exitFullscreen();
                    } else {
                      containerRef.current.requestFullscreen();
                    }

                    setfullscreenEnabled(!fullscreenEnabled);
                  }
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
});
